// Local mock-auth bypass for REMUS dev/e2e.
//
// REMUS does NOT use the skeleton's LocalAuthenticationFilter; it relies on
// DevSecurityConfig's RequestHeaderAuthenticationFilter which reads ADFS_LOGIN
// and DEBUG_ROLES from request headers. This module just gates when the axios
// interceptor should switch to that path.

type MockAuthTokens = NonNullable<Window['__E2E_KC_TOKENS__']>;

const TOKEN_STORAGE_KEY = '__E2E_KC_TOKENS__';
const LOCAL_AUTH_LOGIN = 'local.user';
export const ELECTRON_APP_ORIGIN = 'http://localhost:34115';

const getConfiguredMockAuthLogin = (): string =>
  (import.meta.env.VITE_LOCAL_AUTH_LOGIN as string | undefined) ?? LOCAL_AUTH_LOGIN;

const base64Url = (value: object): string =>
  btoa(JSON.stringify(value)).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');

const token = (payload: object): string =>
  `${base64Url({ alg: 'none', typ: 'JWT' })}.${base64Url(payload)}.`;

const createLocalMockAuthTokens = (): MockAuthTokens => {
  const now = Math.floor(Date.now() / 1000);
  const login = getConfiguredMockAuthLogin();
  const payload = {
    exp: now + 3600,
    iat: now,
    auth_time: now,
    iss: 'https://auth.cern.ch/auth/realms/cern',
    aud: window.REACT_APP_KEYCLOAK_CLIENT_ID || 'remus-dev',
    sub: 'local-admin',
    typ: 'Bearer',
    azp: window.REACT_APP_KEYCLOAK_CLIENT_ID || 'remus-dev',
    cern_upn: login,
    preferred_username: login,
    name: login,
    given_name: login,
    family_name: '',
    email: `${login}@cern.ch`,
    cern_person_id: '100000',
    cern_preferred_language: 'EN',
    cern_roles: ['admin', 'user'],
  };

  return {
    token: token(payload),
    refreshToken: token({ ...payload, typ: 'Refresh', exp: now + 7200 }),
    idToken: token({ ...payload, typ: 'ID' }),
  };
};

export const readStoredMockAuthTokens = (): MockAuthTokens | undefined => {
  const serializedTokens = window.sessionStorage.getItem(TOKEN_STORAGE_KEY);
  if (!serializedTokens) {
    return undefined;
  }
  try {
    return JSON.parse(serializedTokens) as MockAuthTokens;
  } catch {
    return undefined;
  }
};

// App packaging (Electron) ships a *production* Vite build, so `import.meta.env.DEV`
// is false there. App flags are honoured only on the Electron localhost origin;
// deployed web builds must stay governed by runtime config/init.js values.
export const isElectronAppOrigin = (origin: string = window.location.origin): boolean =>
  origin === ELECTRON_APP_ORIGIN;

export const isAppMode = (origin: string = window.location.origin): boolean =>
  import.meta.env.VITE_APP === 'true' && isElectronAppOrigin(origin);

const isKerberosAppAuthMode = (): boolean =>
  (window.REMUS_APP_AUTH_MODE ?? 'kerberos').toLowerCase() === 'kerberos';

// The app SSO build (Electron) does NOT use the header bypass: it authenticates
// the OS-logged-in CERN user via Kerberos/SPNEGO against CERN SSO. VITE_APP_SSO
// drives KeycloakProvider to pass kc_idp_hint=kerberos only on the Electron origin.
export const isAppSsoMode = (origin: string = window.location.origin): boolean =>
  import.meta.env.VITE_APP_SSO === 'true' && isElectronAppOrigin(origin) && isKerberosAppAuthMode();

export const isMockAuthEnabled = (): boolean =>
  (import.meta.env.DEV || isAppMode()) &&
  import.meta.env.VITE_LOCAL_AUTH_BYPASS === 'true';

export const isLocalAuthBypassEnabled = (): boolean => isMockAuthEnabled();

export const getMockAuthTokens = (): MockAuthTokens | undefined => {
  if (!isMockAuthEnabled()) {
    return undefined;
  }

  if (window.__E2E_KC_TOKENS__) {
    return window.__E2E_KC_TOKENS__;
  }

  const storedTokens = readStoredMockAuthTokens();
  if (storedTokens) {
    return storedTokens;
  }

  const tokens = createLocalMockAuthTokens();
  window.__E2E_KC_TOKENS__ = tokens;
  window.sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  return tokens;
};

export const getMockAuthRoles = (): string =>
  (import.meta.env.VITE_LOCAL_AUTH_ROLES as string | undefined) ?? 'ROLE_ADMIN';

export const getMockAuthLogin = (): string => getConfiguredMockAuthLogin();

export const getMockAuthStompHeaders = (): Record<string, string> | undefined =>
  isLocalAuthBypassEnabled()
    ? {
        ADFS_LOGIN: getMockAuthLogin(),
        DEBUG_ROLES: getMockAuthRoles(),
      }
    : undefined;
